import { TableCell } from '@/components/Datatable/TableCell';
import { IDatatableData } from '@/type/listing.types';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { AddressDisplay } from '@/components/AddressDisplay';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { Avatar } from '@/components/Avatar';
import { StatusTag } from '@/components/Tag/StatusTag';

export const BranchTableData = <TSchema extends FieldValues>({
  t,
  data,
  pendingDeactivate,
  onDeactivate,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
  });

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={8}
          text={numberOfItemsText}
          disableDeactivate={selectedTableData?.every((item) => !item?.active)}
          disableDownload
          onDeactivate={handleDeactivate}
          onDownload={() => {}}
          onCreate={() => navigate('/branch/create')}
        />
      )}
      {data.map((entry: TSchema) => {
        return (
          <tr
            key={entry?.id}
            className={twMerge('', !entry?.active && 'tr-deactivated')}
          >
            <TableCell
              tdClassName="sticky-select-column"
              content={
                <Checkbox
                  checked={Boolean(
                    selectedTableData?.find((item) => item?.id === entry?.id)
                  )}
                  onChange={() => onSelectedItem(entry?.id)}
                />
              }
            />
            <TableCell content={entry?.name} />
            <TableCell
              content={
                entry?.company && (
                  <div className="flex items-center gap-2">
                    <Avatar
                      imageUrl={entry?.company?.logo}
                      containerClassName="w-[33px] h-[33px] border-0 shadow-0 rounded-lg [&>.profile-image-wrapper]:rounded-md"
                    />
                    <div className="flex items-center text-[13.5px] leading-[17px]">
                      {entry?.company?.name}
                    </div>
                  </div>
                )
              }
            />
            <TableCell
              content={
                entry?.company?.group && (
                  <div className="flex items-center gap-2">
                    <Avatar
                      imageUrl={entry?.company?.group?.logo}
                      containerClassName="w-[33px] h-[33px] border-0 shadow-0 rounded-lg [&>.profile-image-wrapper]:rounded-md"
                    />
                    <div className="flex items-center text-[13.5px] leading-[17px]">
                      {entry?.company?.group?.name}
                    </div>
                  </div>
                )
              }
            />
            <TableCell
              content={
                <div className="flex flex-wrap justify-center gap-2">
                  {entry?.company?.group?.isDistributor && (
                    <StatusTag
                      text={t('common:distributor')}
                      className="min-w-[90px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                  )}
                  {entry?.company?.group?.isBranch && (
                    <StatusTag
                      text={t('common:branch')}
                      className="min-w-[90px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                  )}
                </div>
              }
            />
            <TableCell
              content={
                <>
                  {AddressDisplay({
                    addressLine: entry?.addressLine1,
                    postalCode: entry?.postalCode,
                    city: entry?.city,
                    country: entry?.country,
                  })}
                </>
              }
            />
            <TableCell
              tdClassName="text-center"
              content={
                <StatusTag
                  text={`${
                    entry?.company?.group?.isWhiteLabel
                      ? t('common:active')
                      : t('common:inactive')
                  }`}
                  className={
                    entry?.company?.group?.isWhiteLabel
                      ? 'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4 text-xs dark-mode:bg-green-EAFFF2/10 dark-mode:border-green-C9F4D2/10 dark-mode:text-green-12C653/75'
                      : 'bg-red-FFEEF4 border-red-FFCAD6 text-red-F8275A text-xs dark-mode:bg-red-FFEEF4/10 dark-mode:border-red-FFCAD6/10 dark-mode:text-red-F8275A/80 dark-mode:tracking-wider'
                  }
                />
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu>
                  <SubMenuItem
                    text={t('common:edit')}
                    leftIconClassName="ki-notepad-edit !text-[16px]"
                    to={`/branch/edit/${entry?.id}`}
                  />
                  <SubMenuItem
                    text={t('common:deactivate')}
                    disabled={!entry?.active || pendingDeactivate}
                    leftIconClassName="ki-trash !text-[19px]"
                    textClassName="line-clamp-1"
                    onClick={() => onDeactivate([entry])}
                  />
                </TableMenu>
              }
            />
          </tr>
        );
      })}
    </>
  );
};
